import React from "react"
import { useTranslation } from "react-i18next"

import { useAlert, useForm, useUser } from "hooks"
import { DocumentsTypes, DocumentToUpload, ErrorResponse, UploadedDocument } from "models"

import { uploadFile } from "utils"
import { BikeIcon, CheckedIcon, CrossIcon, EditIcon, Markdown, Spinner } from "components/Elements"
import { FormElement, formStatusEnum } from "components/Form"
import { BikeDocumentsProps } from "../BikeDocuments"
import "./BikePhoto.scoped.scss"

const defaultFormFields = {
	photo: "",
}

export const BikePhoto: React.FC<BikeDocumentsProps> = ({ bike, documents, refreshDocuments }) => {
	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		showLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields,
		defaultFormStatus: formStatusEnum.readonly,
	})

	const { user } = useUser()
	const { t } = useTranslation()
	const { alert } = useAlert()

	const isLoading = formStatus === formStatusEnum.loading
	const isReadonly = formStatus === formStatusEnum.readonly
	const isReady = formStatus === formStatusEnum.ready

	React.useEffect(() => {
		setFormFields((formFields: UploadedDocument) => ({
			...formFields,
			photo: documents.find(
				(document) => document.document_data.gender === DocumentsTypes.PHOTO
			),
		}))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documents])

	const handleInputPictureChange = async (
		event: React.ChangeEvent<DocumentToUpload & { blob: Blob }>
	) => {
		const value = event.target
		const reader = new FileReader()

		reader.onload = (e) => {
			// Create local url from blob
			value.localUrl = e.target.result

			setFormFields((prevFormFields: UploadedDocument) => ({
				...prevFormFields,
				photo: value,
			}))
		}
		reader.readAsDataURL(value.blob)
	}

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()

		try {
			setFormStatus(formStatusEnum.loading)

			const documentToUpload: DocumentToUpload = {
				bike_id: bike.bike_id,
				account_id: user.account_id,
				document: {
					name: formFields.photo.document.name,
					custom_name: formFields.photo.document.custom_name,
					gender: DocumentsTypes.PHOTO,
				},
			}
			await uploadFile(documentToUpload, formFields.photo.blob)
			alert("success", t("photo_update", { ns: "form" }))
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
		} finally {
			setFormStatus(formStatusEnum.readonly)
			refreshDocuments()
		}
	}

	const resetForm = async (event: React.MouseEvent<HTMLButtonElement>) => {
		setFormStatus(formStatusEnum.readonly)
		// If document has just been uploaded, when cancel, we clean the field
		!formFields.photo?.bike_id && refreshDocuments()
	}

	return (
		<>
			<form className="c-bike-photo">
				{!formFields.photo && (
					<label htmlFor="picture">
						<div
							className={`c-bike-photo__default-image mx-auto ${
								isReady ? "is-active" : ""
							}`}
						>
							<BikeIcon color="var(--placeholderColor)" size="95" className="mr-2" />
						</div>
						{/* <img
							src={process.env.PUBLIC_URL + "/images/bicycle_default.png"}
							alt="Default Bicycle"
						/> */}
					</label>
				)}

				{formFields.photo && (
					<label htmlFor="picture">
						<img
							src={
								formFields.photo?.document_data
									? formFields.photo?.document_data.url
									: formFields.photo?.localUrl
							}
							alt={
								formFields.photo?.document_data
									? formFields.photo?.document_data.custom_name
									: formFields.photo.document.name
							}
							className={`c-bike-photo__image mx-auto ${isReady ? "is-active" : ""}`}
						/>
					</label>
				)}

				<FormElement
					value={formFields.photo}
					name="photo"
					type="file"
					classNameInput="sr-only"
					required={true}
					disabled={isReadonly}
					id="picture"
					acceptedExtensions={["jpg", "JPG", "jpeg", "JPEG", "png", "PNG"]}
					showErrors={showLocalErrors}
					onValidate={handleValidation}
					onChange={handleInputPictureChange}
				/>

				<div className="absolute top-5 right-5">
					{isReady && (
						<div className="flex flex-col items-center">
							<button
								type="submit"
								onClick={handleSubmit}
								className="c-bike-photo__button mb-2"
								aria-label={t("global.save")}
								disabled={!formFields.photo?.document}
							>
								<CheckedIcon size="34" color="var(--secondaryColor)" />
							</button>

							<button
								type="button"
								aria-label={t("global.cancel")}
								onClick={resetForm}
								className="c-bike-photo__button c-bike-photo__button-white"
							>
								<CrossIcon size="16" color="var(--secondaryColor)" />
							</button>
						</div>
					)}

					{isReadonly && (
						<button
							type="button"
							aria-label={t("global.edit")}
							onClick={() => setFormStatus(formStatusEnum.ready)}
							className="c-bike-photo__button c-bike-photo__button-white"
						>
							<EditIcon size="20" color="var(--secondaryColor)" />
						</button>
					)}

					{isLoading && <Spinner color={"var(--primaryColor)"} size={"40px"} />}
				</div>
			</form>

			{isReady && (
				<div className="text-xxs text-center">
					<Markdown>
						{t("documents.picture_format", {
							ns: "bike",
						})}
					</Markdown>
				</div>
			)}
		</>
	)
}
